import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Row, Col, Card, Table, Button, Spinner, Modal, Dropdown, Form } from 'react-bootstrap';
import { format } from 'date-fns';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import Flex from '../../common/Flex';
import Avatar from '../../common/Avatar';
import CardDropdown from '../../common/CardDropdown';
import SearchAccordion from '../../common/SearchAccordion';
import PaginationEllipsis from '../../common/PaginationEllipsis';
import SubtleBadge from '../../common/SubtleBadge';
import { getDocumentStatus } from '../../../lib/api/document';
import {
  cancelContract,
  exportContracts,
  list,
  listContractTerminations,
  remove,
  processContract
} from '../../../lib/api/contract';
import { Add } from './Add';
import { Edit } from './Edit';
import { useAppContext } from '../../../providers/AppProvider';

const formatDateValue = value => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return format(date, 'dd.MM.yyyy');
};

const formatShortDateValue = value => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return format(date, 'dd.MM.yy');
};

export const List = () => {
  const { userApp, refreshNotification } = useAppContext();
  const storedEntitiesGuide = localStorage.getItem('entities_guide');
  const entities_guide =
    storedEntitiesGuide && storedEntitiesGuide !== 'null' && storedEntitiesGuide !== 'undefined'
      ? storedEntitiesGuide
      : undefined;

  const [search, setSearch] = useState({});
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState([]);
  const [mode, setMode] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [contract, setContract] = useState(null);
  const [contractToCancel, setContractToCancel] = useState(null);
  const [terminationOptions, setTerminationOptions] = useState([]);
  const [cancelData, setCancelData] = useState({
    contract_terminations_id: '',
    terminated_at: format(new Date(), 'yyyy-MM-dd')
  });
  const [cancelErrors, setCancelErrors] = useState({
    contract_terminations_id: false,
    terminated_at: false
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paramsObj = Object.fromEntries(queryParams.entries());

  const buildListParams = (currentSearch = search, currentPage = page) => ({
    ...(entities_guide ? { entities_guide } : {}),
    page: currentPage,
    pageSize,
    ...currentSearch
  });

  const loadDocuments = async (currentSearch = search, currentPage = page) => {
    const response = await list(buildListParams(currentSearch, currentPage));
    const rows = response.data || [];

    setDocuments(rows);
    setTotalPages(response.totalPages || 0);

    return rows;
  };

  const handlePageChange = newPage => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleExport = async filters => {
    if (exporting) return;

    setPage(1);
    setSearch(filters);
    setExporting(true);
    try {
      await exportContracts(filters);
    } catch (error) {
      toast.error(error?.message || 'Greška pri izvozu ugovora u Excel.');
    } finally {
      setExporting(false);
    }
  };

  const handleNew = () => {
    setContract(null);
    setMode('add');
    setShowModal(true);
  };

  const handleEdit = contractRow => {
    setContract(contractRow || null);
    setMode('edit');
    setShowModal(true);
  };

  const handleView = contractRow => {
    setContract(contractRow || null);
    setMode('view');
    setShowModal(true);
  };

  const handleOpenCancel = contractRow => {
    setContractToCancel(contractRow);
    setCancelData({
      contract_terminations_id: '',
      terminated_at: format(new Date(), 'yyyy-MM-dd')
    });
    setCancelErrors({
      contract_terminations_id: false,
      terminated_at: false
    });
    setShowCancelModal(true);
  };

  const handleProcess = async (documentGuid, nextStatus) => {
    try {
      const response = await processContract(documentGuid, {
        documents_status_id: nextStatus.documents_status_id
      });

      setDocuments(prev =>
        prev.map(row => (row.document_guid === response.document_guid ? response : row))
      );
      refreshNotification();
    } catch (err) {
      toast.error(
        <span>
          <p>Doslo je do greske.</p>
          <p>{err.body?.message || err.message}</p>
        </span>
      );
    }
  };

  const handleCancelInput = e => {
    const { name, value } = e.target;
    setCancelData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCancelSubmit = async event => {
    event.preventDefault();

    const nextErrors = {
      contract_terminations_id: !cancelData.contract_terminations_id,
      terminated_at: !cancelData.terminated_at
    };

    setCancelErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean) || !contractToCancel?.document_guid) {
      return;
    }

    try {
      const response = await cancelContract(contractToCancel.document_guid, cancelData);
      setDocuments(prev =>
        prev.map(row => (row.document_guid === response.document_guid ? response : row))
      );
      setShowCancelModal(false);
      setContractToCancel(null);
    } catch (err) {
      toast.error(
        <span>
          <p>Doslo je do greske.</p>
          <p>{err.body?.message || err.message}</p>
        </span>
      );
    }
  };

  const handelDelete = async documentGuid => {
    setIsLoading(true);
    try {
      if (confirm('Da li ste sigurni da zelite da izbrisete dokument?')) {
        await remove(documentGuid);
        setDocuments(prev => prev.filter(row => row.document_guid !== documentGuid));
      }
    } catch (error) {
      console.error('Failed to delete item', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const [documentsResponse, statusResponse] = await Promise.all([
          list(buildListParams(search, page)),
          getDocumentStatus({ document_type_id: 12 })
        ]);

        setDocuments(documentsResponse.data || []);
        setTotalPages(documentsResponse.totalPages || 0);
        setStatus(statusResponse || []);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [entities_guide, page, pageSize, search]);

  useEffect(() => {
    (async () => {
      try {
        const response = await listContractTerminations();
        setTerminationOptions(response || []);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const findStatus = id => {
    const statusId = id === 20 ? 21 : 20;
    return status.find(item => item.documents_status_id === statusId);
  };

  const renderActions = item => {
    const nextStatus = findStatus(item.documents_status_id);

    return (
      <CardDropdown>
        <div className="py-2">
          <Dropdown.Item onClick={() => handleView(item)}>
            Pregled dokumenta
          </Dropdown.Item>
          {(item.documents_status_id === 20 ||
            (userApp.role_type_id === 'admin' && item.documents_status_id === 21)) && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => handleEdit(item)}>
                Izmjena
              </Dropdown.Item>
            </>
          )}
          {userApp.role_type_id === 'admin' &&
            item.documents_status_id === 22 &&
            !item.contract_terminations_id && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => handleOpenCancel(item)}>Raskid ugovora</Dropdown.Item>
              </>
            )}
          {userApp.role_type_id === 'clubs' ? (
            nextStatus &&
            item.documents_status_id !== 22 && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => handleProcess(item.document_guid, nextStatus)}>
                  {nextStatus.name}
                </Dropdown.Item>
              </>
            )
          ) : (
            <>
              <Dropdown.Divider />
              {status.map((statusItem, statusIndex) => (
                <Dropdown.Item
                  key={statusIndex}
                  onClick={() => handleProcess(item.document_guid, statusItem)}
                >
                  {statusItem.alt_name || statusItem.name}
                </Dropdown.Item>
              ))}
            </>
          )}
          {item.documents_status_id === 20 && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item
                className="text-danger"
                onClick={() => handelDelete(item.document_guid)}
              >
                Delete
              </Dropdown.Item>
            </>
          )}
        </div>
      </CardDropdown>
    );
  };

  return (
    <Row className="g-2">
      <SearchAccordion
        document_type_id={12}
        paramsObj={paramsObj}
        handleExport={handleExport}
        exporting={exporting}
        handleSearch={data => {
          setPage(1);
          setSearch(data);
        }}
      />
      <Col xs={12}>
        <Card className={classNames('h-100')}>
          <Card.Header>
            <Row className={classNames('g-2')}>
              <Col>
                <Flex>
                  <h5 className="mb-0 hover-actions-trigger text-truncate text-nowrap">Ugovori</h5>
                </Flex>
              </Col>
              <Col xs="auto">
                <Button onClick={handleNew} variant="primary">
                  +
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="bg-body-tertiary">
            {isLoading ? (
              <div className="text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <Row className="mb-3">
                <div className="d-none d-xl-block">
                  <Table responsive className="mb-0">
                    <thead>
                      <tr>
                        <th scope="col"></th>
                        <th scope="col"></th>
                        <th scope="col">ID</th>
                        <th scope="col">Datum</th>
                        <th scope="col">Igrac</th>
                        <th scope="col">Vazi do</th>
                        <th scope="col" className="text-center">
                          Status
                        </th>
                        <th scope="col" className="text-end">
                          Akcija
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <div className="ms-2">
                              <Avatar
                                src={`${process.env.REACT_APP_BASE_API_URL}/uploads/${item.image_path}`}
                                size="l"
                                name={item.club_short_name}
                              />
                            </div>
                          </td>
                          <td className="text-nowrap">
                            <div className="d-flex align-items-center">
                              <div className="ms-2">
                                <small>{item.short_name_club || item.club_short_name || '-'}</small>
                              </div>
                            </div>
                          </td>
                          <td>{item.document_id}</td>
                          <td>{formatDateValue(item.date_created)}</td>
                          <td>{item.name_player || '-'}</td>
                          <td>
                            {item.contract_terminations_id ? (
                              <SubtleBadge bg="danger" className="fs-10">
                                {formatShortDateValue(item.terminated_at)}
                              </SubtleBadge>
                            ) : (
                              formatShortDateValue(item.valid_to)
                            )}
                          </td>
                          <td className="text-center">
                            <SubtleBadge bg={item.color} className="fs-10">
                              {item.status_name}
                            </SubtleBadge>
                          </td>
                          <td className="text-end">{renderActions(item)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-xl-none">
                  <Row className="g-3">
                    {documents.map((item, index) => (
                      <Col xs={12} key={item.contracts_guid || item.document_guid || index}>
                        <div
                          className="border rounded-4 bg-white h-100 p-3 shadow-sm cursor-pointer"
                          style={{ borderColor: '#dfe3ee' }}
                          onClick={() => handleView(item)}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-3">
                            <div className="flex-shrink-0">
                              <Avatar
                                src={`${process.env.REACT_APP_BASE_API_URL}/uploads/${item.image_path}`}
                                size="2xl"
                                name={item.club_short_name}
                              />
                            </div>
                            <div
                              className="flex-shrink-0"
                              onClick={event => event.stopPropagation()}
                            >
                              {renderActions(item)}
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="fw-semibold text-dark fs-8">
                              {item.name_player || '-'}
                            </div>

                            <div className="d-flex flex-wrap gap-2 mt-2">
                              <span className="badge rounded-pill badge-subtle-primary">
                                {item.document_id || '-'}
                              </span>
                            </div>

                            <div className="text-700 fs-10 mt-2">
                              {item.club_name || item.name_club || item.short_name_club || item.club_short_name || '-'}
                            </div>

                            <div className="mt-2">
                              <SubtleBadge bg={item.color} className="fs-10">
                                {item.status_name}
                              </SubtleBadge>
                            </div>

                            <div className="text-600 fs-10 mt-2">
                              {formatDateValue(item.date_created)}
                            </div>

                            <div className="text-700 fs-10 mt-1">
                              Vazi do:{' '}
                              {item.contract_terminations_id ? (
                                <span className="text-danger">
                                  {formatShortDateValue(item.terminated_at)}
                                </span>
                              ) : (
                                formatShortDateValue(item.valid_to)
                              )}
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Row>
            )}
          </Card.Body>
          <Card.Footer>
            {totalPages > 1 && (
              <Row className="justify-content-center">
                <PaginationEllipsis
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </Row>
            )}
          </Card.Footer>

          <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>{contract ? 'Ugovor' : 'Novi ugovor'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {contract ? (
                <Edit
                  initContract={contract}
                  initMode={mode}
                  onClose={() => setShowModal(false)}
                  onSave={() => loadDocuments()}
                />
              ) : (
                <Add
                  onClose={() => setShowModal(false)}
                  onSave={() => loadDocuments({}, 1)}
                  onCreated={createdContract => {
                    setContract(createdContract);
                    setMode('edit');
                    setShowModal(true);
                  }}
                />
              )}
            </Modal.Body>
          </Modal>

          <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Ponistenje ugovora</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Card>
                <Card.Body className="bg-body-tertiary p-4">
                  <Form onSubmit={handleCancelSubmit}>
                    <Row className="g-3 mb-3">
                      <Form.Group as={Col} md="12">
                        <Form.Label>Klub</Form.Label>
                        <Form.Control value={contractToCancel?.club_name || '-'} disabled />
                      </Form.Group>
                      <Form.Group as={Col} md="12">
                        <Form.Label>Igrac</Form.Label>
                        <Form.Control value={contractToCancel?.name_player || '-'} disabled />
                      </Form.Group>
                      <Form.Group as={Col} md="12">
                        <Form.Label>Vazi do</Form.Label>
                        <Form.Control
                          value={formatDateValue(contractToCancel?.valid_to) || '-'}
                          disabled
                        />
                      </Form.Group>
                    </Row>

                    <div className="border-dashed border-bottom my-3" />

                    <Form.Group className="mb-3" controlId="contract_terminations_id">
                      <Form.Label>Razlog raskida</Form.Label>
                      <Form.Select
                        name="contract_terminations_id"
                        value={cancelData.contract_terminations_id}
                        onChange={handleCancelInput}
                        isInvalid={cancelErrors.contract_terminations_id}
                      >
                        <option value="">Izaberi</option>
                        {terminationOptions.map(option => (
                          <option
                            key={option.contract_terminations_id}
                            value={option.contract_terminations_id}
                          >
                            {option.name || option.contract_termination_name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Molimo izaberite razlog raskida.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="terminated_at">
                      <Form.Label>Datum raskida</Form.Label>
                      <Form.Control
                        type="date"
                        name="terminated_at"
                        value={cancelData.terminated_at}
                        onChange={handleCancelInput}
                        isInvalid={cancelErrors.terminated_at}
                      />
                      <Form.Control.Feedback type="invalid">
                        Molimo unesite datum raskida.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <div className="border-dashed border-bottom my-3" />
                    <Button type="submit">Sacuvaj</Button>
                  </Form>
                </Card.Body>
              </Card>
            </Modal.Body>
          </Modal>
        </Card>
      </Col>
    </Row>
  );
};

export default List;
